import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';

import Notifications from 'react-notify-toast';
import { theme } from 'ui-kit';
import { Footer } from './components/footer';
import Navbar from './components/NavBar/navbar';
// import utils and check for IE
import useCurrentUserPermissions from 'hooks/useCurrentUserPermissions';
import { usePostHogPageView } from 'hooks/usePostHogPageView';
import { useRouteMatch } from 'react-router-dom';
import { startRecurringAuthCheck } from './utils/authutils';
import { getInternetExplorerVersion } from './utils/util';
import { CandidateNavBar } from 'components/NavBar/CandidateNavbar/CandidateNavBar';
import { BannerManager } from 'features/Connect/components/Banner/BannerManager';
import {
  BannerContextProvider,
  useBannerContext,
} from 'features/Connect/features/Context/BannerContextProvider';
import {
  NAVBAR_HEIGHT_DESKTOP,
  NAVBAR_HEIGHT_MOBILE,
} from 'features/Connect/components/Banner/constants';
import { PrincipalNavbar } from 'features/Principal/Navbar';

interface LayoutProps {
  children: React.ReactNode;
}

type SetNotFoundContextValue = () => () => void;

const SetNotFoundContext = createContext<SetNotFoundContextValue>(() => () => {});

export const useSetNotFound = () => useContext(SetNotFoundContext);

declare global {
  interface Analytics {
    page: () => void;
  }

  interface Window {
    analytics: Analytics;
  }
}

function LayoutContent({ children, shouldRenderCandidateNavBar }) {
  const { bannerHeight } = useBannerContext();

  return (
    <Box
      sx={{
        flex: 1,
        ...(shouldRenderCandidateNavBar && {
          paddingTop: {
            xs: `${NAVBAR_HEIGHT_MOBILE + bannerHeight}px`,
            lg: `${NAVBAR_HEIGHT_DESKTOP + bannerHeight}px`,
          },
          transition: 'padding-top 0.3s cubic-bezier(0.4,0,0.2,1)',
        }),
      }}
    >
      {children}
    </Box>
  );
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [notFound, setNotFound] = useState(false);
  const { userType, hasApplications } = useCurrentUserPermissions();
  const location = useLocation();
  usePostHogPageView();

  const handleSetNotFound = useCallback(() => {
    setNotFound(true);

    return () => {
      setNotFound(false);
    };
  }, []);

  const jobMatch = useRouteMatch('/jobs/');
  const onLoginPage = useRouteMatch('/login');
  const onJobDetailsPage = useRouteMatch('/jobs/:id/details');
  const shouldRenderNavBar = (!jobMatch && !notFound && !onLoginPage) || onJobDetailsPage;
  const atConnectRoute = useRouteMatch('/connect');
  const atCandidatePortalRoute = useRouteMatch('/candidate/:id');
  const atConnectOrCandidatePortalRoute = !!atConnectRoute || !!atCandidatePortalRoute;
  const atATSConnectIntegrationRoute = useRouteMatch('/connect/import-candidates');
  const atDistrictJobsList = useRouteMatch('/district/jobslist');
  const atSchoolJobsList = useRouteMatch('/school/jobslist');
  const atTrevorDashboards = useRouteMatch('/data/onboarding-usage');
  const atPrincipalCandidateList = useRouteMatch('/principal/candidates')?.isExact;
  const atPrincipalHiringRequests = useRouteMatch('/principal/hiring-requests')?.isExact;
  const atPrincipalRoute = atPrincipalCandidateList || atPrincipalHiringRequests;
  const shouldNotShowFooter =
    atTrevorDashboards || atDistrictJobsList || atSchoolJobsList || atCandidatePortalRoute;
  const shouldRenderCandidateNavBar =
    !onLoginPage && atConnectOrCandidatePortalRoute && !atATSConnectIntegrationRoute;

  useEffect(() => {
    if (window.analytics) {
      window.analytics.page();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    // Check for IE and add class
    if (getInternetExplorerVersion() !== -1) {
      document.body.classList.add('ie');
    }

    startRecurringAuthCheck();
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <BannerContextProvider>
        <Notifications />
        {shouldRenderCandidateNavBar && <CandidateNavBar />}
        {atPrincipalRoute && <PrincipalNavbar />}
        {!shouldRenderCandidateNavBar && !atPrincipalRoute && (
          <Navbar
            show={shouldRenderNavBar}
            onProspectList={window.location.href.indexOf('prospectlist') !== -1}
            onProspectEntry={window.location.href.indexOf('prospectentry') !== -1}
            userType={userType}
            hasApplications={hasApplications}
          />
        )}
        <BannerManager />
        <LayoutContent shouldRenderCandidateNavBar={shouldRenderCandidateNavBar}>
          <SetNotFoundContext.Provider value={handleSetNotFound}>
            {children}
          </SetNotFoundContext.Provider>
        </LayoutContent>
        {!shouldNotShowFooter && <Footer />}
      </BannerContextProvider>
    </ThemeProvider>
  );
};

export default Layout;
